
import * as THREE from "../../libs/three.js/build/three.module.js";
import {GeoJSONExporter} from "../exporter/GeoJSONExporter.js"
import {DXFExporter} from "../exporter/DXFExporter.js"
import {Utils} from "../utils.js"
import {HierarchicalSlider} from "./HierarchicalSlider.js"

import JSON5 from "../../libs/json5-2.1.3/json5.mjs";
import {DatumAnchorPanel} from "./PropertyPanels/DatumAnchorPanel.js";
import {LandmarkPopover} from "./LandmarkPopover.js";

export class Sidebar{

	constructor(viewer){
		this.viewer = viewer;
		this.measuringTool = viewer.measuringTool;
		this.dom = $("#sidebar_root");
		this.landmarkPopover = new LandmarkPopover(viewer);
	}

	createToolIcon(icon, title, callback){
		let element = $(`
			<img src="${icon}"
				class="button-icon"
				data-i18n="${title}" />
		`);
		element.click(callback);
		return element;
	}

	init(){
		this.initToolbar();
		this.initToolbarActions();
		this.initSpeedSlider();
		this.initClassificationChips();
		this.initFilters();
		this.initLandmarks();
		this.initExport();
		this.initPopovers();
	}

	// ========================================
	// TOOLBAR (measurement tool icons)
	// ========================================

	initToolbar(){
		let elToolbar = $('#toolbar_tools');

		// DISTANCE
		elToolbar.append(this.createToolIcon(
			Potree.resourcePath + '/icons/distance.svg',
			'[title]tt.distance_measurement',
			() => {
				this.measuringTool.startInsertion({
					showDistances: true,
					showArea: false,
					closed: false,
					name: 'Distance'});
			}
		));

		// HEIGHT
		elToolbar.append(this.createToolIcon(
			Potree.resourcePath + '/icons/height.svg',
			'[title]tt.height_measurement',
			() => {
				this.measuringTool.startInsertion({
					showDistances: false,
					showHeight: true,
					showArea: false,
					closed: false,
					maxMarkers: 2,
					name: 'Height'});
			}
		));

		// ELEVATION ZONE
		elToolbar.append(this.createToolIcon(
			Potree.resourcePath + '/icons/elevation.svg',
			'[title]tt.elevation_zone',
			() => {
				this.viewer.elevationZoneTool.startInsertion();
			}
		));

		// CURB REVEAL
		elToolbar.append(this.createToolIcon(
			Potree.resourcePath + '/icons/curb_reveal.svg',
			'[title]tt.curb_reveal',
			() => {
				this.viewer.curbRevealTool.startInsertion();
			}
		));

		// CROSS-SECTION PROFILE
		elToolbar.append(this.createToolIcon(
			Potree.resourcePath + '/icons/cross_section.svg',
			'[title]tt.cross_section',
			() => {
				this.viewer.crossSectionTool.startInsertion();
			}
		));

		// REMOVE ALL
		elToolbar.append(this.createToolIcon(
			Potree.resourcePath + '/icons/reset_tools.svg',
			'[title]tt.remove_all_measurement',
			() => {
				this.viewer.dispatchEvent({type: 'cancel_insertions'});
				this.viewer.scene.removeAllMeasurements();
			}
		));
	}

	// ========================================
	// TOOLBAR ACTIONS (filter + landmark triggers)
	// ========================================

	initToolbarActions(){
		let elActions = $('#toolbar_actions');

		// Filter trigger
		let filterIcon = Potree.resourcePath + '/icons/filter.svg';
		let filterBtn = $(`<img src="${filterIcon}" class="toolbar-action-btn" title="Filters" />`);
		filterBtn.on('click', (e) => {
			e.stopPropagation();
			$('#cyvl_filter_popover').toggle();
			$('#cyvl_landmark_palette').hide();
			filterBtn.toggleClass('active', $('#cyvl_filter_popover').is(':visible'));
			if (this._landmarkBtn) this._landmarkBtn.removeClass('active');
		});
		elActions.append(filterBtn);

		// Landmark trigger (hidden until zones exist)
		let landmarkIcon = Potree.resourcePath + '/icons/landmark.svg';
		let landmarkBtn = $(`
			<div style="position: relative; display: none;">
				<img src="${landmarkIcon}" class="toolbar-action-btn" title="Landmarks" />
				<span class="toolbar-badge" id="landmark_count_badge">0</span>
			</div>
		`);
		landmarkBtn.on('click', (e) => {
			e.stopPropagation();
			$('#cyvl_landmark_palette').toggle();
			$('#cyvl_filter_popover').hide();
			landmarkBtn.find('.toolbar-action-btn').toggleClass('active', $('#cyvl_landmark_palette').is(':visible'));
			filterBtn.removeClass('active');
		});
		elActions.append(landmarkBtn);

		this._filterBtn = filterBtn;
		this._landmarkBtn = landmarkBtn;

		// Show landmark button when zones exist
		let updateLandmarkBtnVisibility = () => {
			let count = this.viewer.scene.featureZones.length;
			landmarkBtn.css('display', count > 0 ? '' : 'none');
			$('#landmark_count_badge').text(count);
		};

		this.viewer.scene.addEventListener('feature_zone_added', updateLandmarkBtnVisibility);
		this.viewer.scene.addEventListener('feature_zone_removed', updateLandmarkBtnVisibility);
		updateLandmarkBtnVisibility();
	}

	// ========================================
	// SPEED SLIDER (vertical, in toolbar)
	// ========================================

	initSpeedSlider(){
		$('#speed_icon').attr('src', Potree.resourcePath + '/icons/speedometer.svg');

		let sldMoveSpeed = $('#sldMoveSpeed');
		let lblMoveSpeed = $('#lblMoveSpeed');

		// Simple linear 0-100 scale
		let clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

		sldMoveSpeed.slider({
			orientation: "vertical",
			value: clamp(this.viewer.getMoveSpeed(), 0, 100),
			min: 0,
			max: 100,
			step: 1,
			slide: (event, ui) => { this.viewer.setMoveSpeed(ui.value); }
		});

		this.viewer.addEventListener('move_speed_changed', (event) => {
			let speed = this.viewer.getMoveSpeed();
			lblMoveSpeed.html(Math.round(speed));
			sldMoveSpeed.slider({value: clamp(speed, 0, 100)});
		});

		lblMoveSpeed.html(Math.round(this.viewer.getMoveSpeed()));
	}

	// ========================================
	// CLASSIFICATION CHIPS (top center)
	// ========================================

	initClassificationChips(){
		let initialized = false;
		let elContainer = $('#cyvl_classification_chips');
		let soloClassId = null;
		let savedVisibility = {};

		let scanClassifications = () => {
			let uniqueClasses = new Set();
			for (let pointcloud of this.viewer.scene.pointclouds) {
				if (!pointcloud.visibleNodes) continue;
				for (let node of pointcloud.visibleNodes) {
					if (node.sceneNode &&
						node.sceneNode.geometry &&
						node.sceneNode.geometry.attributes.classification) {
						let array = node.sceneNode.geometry.attributes.classification.array;
						for (let i = 0; i < array.length; i++) {
							uniqueClasses.add(array[i]);
						}
					}
				}
			}
			return Array.from(uniqueClasses).sort((a, b) => a - b);
		};

		let buildChips = (classIds) => {
			elContainer.empty();
			let classifications = this.viewer.classifications || {};
			let fallback = classifications['DEFAULT'] || {};

			for (let id of classIds) {
				let entry = classifications[id] || fallback;
				let name = (entry && entry.name) || `class ${id}`;
				let color = (entry && entry.color) || [0.5, 0.5, 0.5, 1.0];
				let r = Math.round(color[0] * 255);
				let g = Math.round(color[1] * 255);
				let b = Math.round(color[2] * 255);
				let visible = !entry || entry.visible !== false;

				let chip = $(`
					<span class="classification-chip${visible ? '' : ' disabled'}" data-class-id="${id}">
						<span class="chip-swatch" style="background: rgb(${r},${g},${b});"></span>
						<span>${name}</span>
					</span>
				`);

				chip.on('click', (e) => {
					if (e.altKey) {
						// Solo mode: alt+click
						if (soloClassId === id) {
							// Restore all saved visibility states
							for (let [savedId, savedVisible] of Object.entries(savedVisibility)) {
								this.viewer.setClassificationVisibility(Number(savedId), savedVisible);
							}
							soloClassId = null;
							savedVisibility = {};
							// Refresh all chip states
							elContainer.find('.classification-chip').each(function() {
								let chipId = Number($(this).attr('data-class-id'));
								let current = classifications[chipId];
								$(this).toggleClass('disabled', !(current && current.visible !== false));
							});
						} else {
							// Save current state and solo this class
							savedVisibility = {};
							for (let cid of classIds) {
								let current = classifications[cid];
								savedVisibility[cid] = current && current.visible !== false;
							}
							for (let cid of classIds) {
								this.viewer.setClassificationVisibility(cid, cid === id);
							}
							soloClassId = id;
							elContainer.find('.classification-chip').each(function() {
								let chipId = Number($(this).attr('data-class-id'));
								$(this).toggleClass('disabled', chipId !== id);
							});
						}
					} else {
						// Normal toggle
						soloClassId = null;
						savedVisibility = {};
						let current = this.viewer.classifications[id];
						let newVisible = !(current && current.visible !== false);
						this.viewer.setClassificationVisibility(id, newVisible);
						chip.toggleClass('disabled', !newVisible);
					}
				});

				elContainer.append(chip);
			}
		};

		this.viewer.addEventListener("update", () => {
			if (initialized) return;

			let pointclouds = this.viewer.scene.pointclouds;
			if (pointclouds.length === 0) return;

			let hasEnoughData = false;
			for (let pc of pointclouds) {
				if (pc.progress >= 0.05) {
					hasEnoughData = true;
					break;
				}
			}
			if (!hasEnoughData) return;

			let classIds = scanClassifications();
			if (classIds.length === 0) return;

			buildChips(classIds);
			elContainer.fadeIn(300);
			initialized = true;
		});
	}

	// ========================================
	// FILTERS (range, intensity, Z)
	// ========================================

	initFilters(){
		this.initRangeFilters();
		this.initIntensityFilters();
		this.initZFilters();
	}

	initRangeFilters(){
		let slider = new HierarchicalSlider({
			levels: 1,
			slide: (event) => {
				this.viewer.setFilterRangeRange(...event.values);
			},
		});

		let initialized = false;

		let initialize = () => {
			let elRangeContainer = $("#range_multilevel_range_container");
			elRangeContainer[0].prepend(slider.element);

			let extent = this.viewer.getRangeExtent();
			let defaultMin = 2.0;
			let defaultValues = [Math.max(extent[0], defaultMin), extent[1]];

			slider.setRange(extent);
			slider.setValues(defaultValues);
			this.viewer.setFilterRangeRange(...defaultValues);

			initialized = true;
		};

		this.viewer.addEventListener("update", (e) => {
			let extent = this.viewer.getRangeExtent();
			let rangeAvailable = extent[0] !== Infinity;

			if(rangeAvailable){
				$('#range_filter_container').css('display', '');
			}

			if(!initialized && rangeAvailable){
				initialize();
			}

			slider.setRange(extent);
		});
	}

	initIntensityFilters(){
		let slider = new HierarchicalSlider({
			levels: 1,
			label: "Intensity",
			slide: (event) => {
				this.viewer.setFilterIntensityRange(...event.values);
			},
		});

		let initialized = false;

		let initialize = () => {
			let elContainer = $("#intensity_filter_range_container");
			elContainer[0].prepend(slider.element);

			let extent = this.viewer.getIntensityExtent();
			slider.setRange(extent);
			slider.setValues(extent);
			this.viewer.setFilterIntensityRange(...extent);

			initialized = true;
		};

		this.viewer.addEventListener("update", (e) => {
			let extent = this.viewer.getIntensityExtent();
			let available = extent[0] !== Infinity;

			if(available){
				$('#intensity_filter_container').css('display', '');
			}

			if(!initialized && available){
				initialize();
			}

			slider.setRange(extent);
		});
	}

	initZFilters(){
		let slider = new HierarchicalSlider({
			levels: 1,
			label: "Elevation (Z)",
			slide: (event) => {
				this.viewer.setFilterZRange(...event.values);
			},
		});

		let initialized = false;

		let initialize = () => {
			let elContainer = $("#z_filter_range_container");
			elContainer[0].prepend(slider.element);

			let extent = this.viewer.getZExtent();
			slider.setRange(extent);
			slider.setValues(extent);
			this.viewer.setFilterZRange(...extent);

			initialized = true;
		};

		this.viewer.addEventListener("update", (e) => {
			let extent = this.viewer.getZExtent();
			let available = extent[0] !== Infinity;

			if(available){
				$('#z_filter_container').css('display', '');
			}

			if(!initialized && available){
				initialize();
			}

			slider.setRange(extent);
		});
	}

	// ========================================
	// LANDMARKS (palette popover)
	// ========================================

	initLandmarks(){
		let viewer = this.viewer;
		let elList = $('#landmarks_list');
		let distanceTool = viewer.landmarkDistanceTool;

		let addLandmarkItem = (zone) => {
			let label = zone.properties.label || zone.properties.id || zone.name;
			let rulerIcon = Potree.resourcePath + '/icons/ruler.svg';
			let anchorIcon = Potree.resourcePath + '/icons/anchor.svg';

			let isDerived = !!zone.derivedFrom;
			let anchorBtnHtml = isDerived ? '' : `
				<span class="landmark-anchor" title="Datum anchor">
					<img src="${anchorIcon}" style="width: 14px; height: 14px;" />
				</span>
			`;

			let li = $(`
				<li>
					<span class="landmark-name">${label}</span>
					${anchorBtnHtml}
					<span class="landmark-ruler" title="Toggle distance measurement">
						<img src="${rulerIcon}" style="width: 14px; height: 14px;" />
					</span>
					<span class="landmark-datum-indicator" style="display: none; margin-left: 4px; font-size: 12px;" title="Has datum anchor">&#9875;</span>
				</li>
			`);

			// Name click flies to landmark and shows 3D popover
			li.find('.landmark-name').on('click', () => {
				zone.mesh.geometry.computeBoundingBox();
				let box = zone.mesh.geometry.boundingBox.clone();
				box.min.z += zone.mesh.position.z;
				box.max.z += zone.mesh.position.z;
				zone.boundingBox = box;
				viewer.flyToLandmark(zone, 500);
				this.landmarkPopover.show(zone, distanceTool);
			});

			// Ruler icon toggles distance group
			let rulerBtn = li.find('.landmark-ruler');
			rulerBtn.on('click', (e) => {
				e.stopPropagation();
				distanceTool.toggleInGroup(zone);
			});

			// Anchor icon opens datum anchor panel
			let anchorBtn = li.find('.landmark-anchor');
			let activePanel = null;
			anchorBtn.on('click', (e) => {
				e.stopPropagation();

				if (activePanel) {
					activePanel.elContent.remove();
					activePanel = null;
					anchorBtn.css('background', '');
					return;
				}

				anchorBtn.css('background', 'var(--muted)');
				let panel = new DatumAnchorPanel(viewer, zone);
				panel.onClose = () => {
					activePanel = null;
					anchorBtn.css('background', zone.datumAnchor ? 'var(--cyvl-utility-blue)' : '');
				};
				li.append(panel.elContent);
				activePanel = panel;
			});

			// Update datum indicator when datum changes
			let datumIndicator = li.find('.landmark-datum-indicator');
			let updateDatumIndicator = () => {
				datumIndicator.css('display', zone.datumAnchor ? 'inline' : 'none');
				if (!activePanel) {
					anchorBtn.css('background', zone.datumAnchor ? 'var(--cyvl-utility-blue)' : '');
				}
			};
			zone.addEventListener('datum_anchor_changed', updateDatumIndicator);
			updateDatumIndicator();

			li.data('featureZone', zone);
			elList.append(li);
		};

		// Update ruler button states when group changes
		distanceTool.addEventListener("group_changed", () => {
			elList.children('li').each(function() {
				let zone = $(this).data('featureZone');
				let rulerBtn = $(this).find('.landmark-ruler');
				if (distanceTool.isInGroup(zone)) {
					rulerBtn.css('background', 'var(--cyvl-utility-blue)');
					rulerBtn.find('img').css('filter', 'invert(1)');
				} else {
					rulerBtn.css('background', '');
					rulerBtn.find('img').css('filter', '');
				}
			});

			// Update selection count in footer
			let count = distanceTool.distanceGroup ? distanceTool.distanceGroup.size : 0;
			$('#landmark_selection_info').text(count + ' selected');
		});

		let removeLandmarkItem = (zone) => {
			elList.children('li').each(function() {
				if ($(this).data('featureZone') === zone) {
					$(this).remove();
				}
			});
		};

		// Pick up zones already loaded
		for (let zone of viewer.scene.featureZones) {
			addLandmarkItem(zone);
		}

		// Listen for new zones
		viewer.scene.addEventListener('feature_zone_added', (e) => {
			addLandmarkItem(e.featureZone);
		});

		viewer.scene.addEventListener('feature_zone_removed', (e) => {
			removeLandmarkItem(e.featureZone);
		});

		// Search/filter
		$('#landmark_search').on('input', function() {
			let query = $(this).val().toLowerCase();
			elList.children('li').each(function() {
				let name = $(this).find('.landmark-name').text().toLowerCase();
				$(this).css('display', name.includes(query) ? '' : 'none');
			});
		});

		// Footer controls
		$('#btnToggleDistances').on('click', function() {
			let isVisible = distanceTool.visible;
			distanceTool.setVisible(!isVisible);
			$(this).html(isVisible ? '&#128065;' : '&#128065;');
			$(this).css('opacity', isVisible ? '0.4' : '0.7');
		});

		$('#btnClearDistances').on('click', () => {
			distanceTool.clearGroup();
		});
	}

	// ========================================
	// EXPORT (dropdown)
	// ========================================

	initExport(){
		$('#btn_export_geojson').on('click', () => {
			let scene = this.viewer.scene;
			let measurements = [...scene.measurements, ...scene.profiles, ...scene.volumes];

			if(measurements.length > 0){
				let geoJson = GeoJSONExporter.toString(measurements);
				this._triggerDownload(geoJson, 'measurements.geojson', 'application/json');
			} else {
				this.viewer.postError("No measurements to export");
			}
			$('#cyvl_export_dropdown').hide();
		});

		$('#btn_export_dxf').on('click', () => {
			let scene = this.viewer.scene;
			let measurements = [...scene.measurements, ...scene.profiles, ...scene.volumes];

			if(measurements.length > 0){
				let dxf = DXFExporter.toString(measurements);
				this._triggerDownload(dxf, 'measurements.dxf', 'application/dxf');
			} else {
				this.viewer.postError("No measurements to export");
			}
			$('#cyvl_export_dropdown').hide();
		});

		$('#btn_export_json').on('click', () => {
			let data = Potree.saveProject(this.viewer);
			let dataString = JSON5.stringify(data, null, "\t");
			this._triggerDownload(dataString, 'project.json5', 'application/json');
			$('#cyvl_export_dropdown').hide();
		});
	}

	_triggerDownload(content, filename, mimeType) {
		let blob = new Blob([content], { type: mimeType });
		let url = URL.createObjectURL(blob);
		let a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}

	// ========================================
	// POPOVERS (click-outside-to-close)
	// ========================================

	initPopovers(){
		$(document).on('click', (e) => {
			let target = $(e.target);

			// Close export dropdown
			if (!target.closest('#cyvl_export_dropdown, #btn_export').length) {
				$('#cyvl_export_dropdown').hide();
			}

			// Close landmark palette
			if (!target.closest('#cyvl_landmark_palette, #toolbar_actions').length) {
				$('#cyvl_landmark_palette').hide();
				if (this._landmarkBtn) this._landmarkBtn.find('.toolbar-action-btn').removeClass('active');
			}

			// Close filter popover
			if (!target.closest('#cyvl_filter_popover, #toolbar_actions').length) {
				$('#cyvl_filter_popover').hide();
				if (this._filterBtn) this._filterBtn.removeClass('active');
			}
		});

		// Escape closes all popovers
		$(document).on('keydown', (e) => {
			if (e.key === 'Escape') {
				$('#cyvl_export_dropdown').hide();
				$('#cyvl_landmark_palette').hide();
				$('#cyvl_filter_popover').hide();
				if (this._filterBtn) this._filterBtn.removeClass('active');
				if (this._landmarkBtn) this._landmarkBtn.find('.toolbar-action-btn').removeClass('active');
				this.landmarkPopover.hide();
			}
		});
	}
}
